/**
 * The referrer step — "Who introduced you to Prüvit?" — for every form that
 * asks it. snippets/pruvit-referrer-step.liquid draws it; this runs it.
 *
 * Moved out of pruvit-lead.js on 2026-10-05 so the Pro application could ask
 * the same question without a second copy. A form calls
 *
 *   const step = await window.pruvitReferrerStep.mount(scope, sdk);
 *
 * and gets back null when the step is not on screen, or { has, require }:
 * has() says whether a referrer is settled, and require() is what a form's
 * submit gate calls when one is not — the reason, the scroll and the caret, all
 * on the step itself. Craig, 2026-10-05: a form that creates a customer must
 * not submit without a referrer while MV Qore's Referrer Bypass is off, and the
 * button stays live and explains itself rather than greying out.
 */
(() => {
  // The snippet emits its script tag once per form, so two forms on a page
  // load this twice. The second run must not redefine a mounted API.
  if (window.pruvitReferrerStep) return;

  /*
   * The referrer is one fact and several things draw it — this step and the
   * header bar. Each used to resolve it once at startup and paint from its own
   * copy, so validating a code here left the bar still asking until a reload.
   * Declared here and in pruvit-referrer.js rather than shared, because there
   * is no module boundary between them; the string is the contract.
   */
  const REFERRER_EVENT = 'pruvit:referrer';
  const announceReferrer = (result) =>
    document.dispatchEvent(new CustomEvent(REFERRER_EVENT, { detail: { result: result || null } }));

  /**
   * The `t` filter HTML-escapes what it interpolates, and these strings are
   * read back out of a JSON script tag and written with textContent — which
   * does not decode entities. Without this a name with an apostrophe arrives
   * as isn&#39;t on screen. Same trap the referrer bar hit.
   */
  const decodeEntities = (value) => {
    if (typeof value !== 'string' || !value.includes('&')) return value;
    const box = document.createElement('textarea');
    box.innerHTML = value;
    return box.value;
  };

  const readStrings = (step) => {
    const tag = step.querySelector('[data-pruvit-ref-strings]');
    try {
      return tag ? JSON.parse(tag.textContent) : {};
    } catch (e) {
      return {};
    }
  };

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const mount = async (scope, sdk) => {
    const step = scope && scope.querySelector('[data-pruvit-ref]');
    if (!step || !sdk) return null;

    const strings = readStrings(step);
    const say = (key, fallback) => strings[key] || fallback;

    const el = {
      ask: step.querySelector('[data-pruvit-ref-ask]'),
      input: step.querySelector('[data-pruvit-ref-input]'),
      check: step.querySelector('[data-pruvit-ref-check]'),
      err: step.querySelector('[data-pruvit-ref-err]'),
      none: step.querySelector('[data-pruvit-ref-none]'),
      noneWrap: step.querySelector('[data-pruvit-ref-none-wrap]'),
      ok: step.querySelector('[data-pruvit-ref-ok]'),
      avatar: step.querySelector('[data-pruvit-ref-avatar]'),
      text: step.querySelector('[data-pruvit-ref-text]'),
      edit: step.querySelector('[data-pruvit-ref-edit]'),
    };

    let hasReferrer = false;
    // MV Qore's Referrer Bypass, read from their settings rather than
    // duplicated as a theme setting. Craig, 2026-09-21: the toggle is the
    // merchant saying whether a shopper may proceed without a referrer, and
    // when it is off checkout enforces the requirement — so offering the
    // choice would walk someone to a wall they cannot see. Null until the
    // settings arrive, which is the safe state.
    let bypass = null;
    const bypassOn = () => !!(bypass && bypass.enabled && bypass.defaultCode);

    const showErr = (message) => {
      el.err.textContent = decodeEntities(message);
      el.err.hidden = false;
    };
    const clearErr = () => {
      el.err.textContent = '';
      el.err.hidden = true;
    };

    /** A record can be valid and still have no name; the code is the fallback. */
    const displayName = (referrer) =>
      ((referrer && referrer.name) || '').trim() || (referrer && referrer.code) || '';

    const paintReferrer = (result) => {
      const referrer = result && result.referrer;
      if (!referrer || !referrer.code) return;
      hasReferrer = true;
      const shown = displayName(referrer);
      el.avatar.textContent = '';
      if (referrer.imageUrl) {
        const img = document.createElement('img');
        img.src = referrer.imageUrl;
        img.alt = '';
        el.avatar.appendChild(img);
      } else {
        el.avatar.textContent = (shown[0] || '?').toUpperCase();
      }
      // Built as nodes so the name can be bold the way the prototype has it,
      // without handing server data to innerHTML. Same reasoning as the
      // referrer bar's writeWithAccent: a text node cannot be talked into
      // being markup.
      el.text.textContent = '';
      const phrase = decodeEntities(say('refWith', 'Referred by %NAME%'));
      const parts = phrase.split('%NAME%');
      el.text.appendChild(document.createTextNode(parts[0] || ''));
      const strong = document.createElement('b');
      strong.textContent = shown;
      el.text.appendChild(strong);
      if (parts.length > 1) el.text.appendChild(document.createTextNode(parts[1] || ''));
      clearErr();
      el.ask.hidden = true;
      el.ok.hidden = false;
    };

    // focus: true when the person pressed Edit on this form — the caret
    // belongs in the field they just asked to change. False when the referrer
    // was cleared somewhere else on the page, because taking focus from
    // whatever they are actually doing to a form they may not have touched is
    // worse than the inconsistency this repaint exists to fix.
    const backToAsk = ({ focus = true } = {}) => {
      hasReferrer = false;
      el.ok.hidden = true;
      el.ask.hidden = false;
      el.none.checked = false;
      clearErr();
      if (focus) el.input.focus();
    };

    // The empty-code message points at the bypass box only when there is one.
    const askMessage = () =>
      bypassOn()
        ? say('refEmpty', 'Enter a code, or tick the box below to continue.')
        : say('refRequired', 'Enter the referrer code of the person who introduced you.');

    const checkCode = async () => {
      const code = (el.input.value || '').trim();
      if (!code) {
        showErr(askMessage());
        return;
      }
      clearErr();
      // aria-busy carries the spinner (see .pruvit-busy in pruvit-tokens) and
      // tells a screen reader the same thing the ring tells everyone else.
      // disabled alone said "not now" rather than "working".
      el.check.classList.add('pruvit-busy');
      el.check.setAttribute('aria-busy', 'true');
      el.check.disabled = true;
      try {
        const result = await sdk.validateReferrer(code);
        // The whole result, never result.referrer — the flattened view drops
        // fields the rest of MV Qore reads back, and passing it throws.
        sdk.persistReferrer(result);
        paintReferrer(result);
        announceReferrer(result);
      } catch (error) {
        const code_ = error && error.code;
        showErr(
          code_ === 'INVALID_REFERRER_CODE'
            ? say('refInvalid', "We couldn't find that code.")
            : code_ === 'RATE_LIMITED'
              ? say('refRate', 'Too many attempts. Try again in a minute.')
              : say('refGeneric', 'Something went wrong. Please try again.')
        );
      } finally {
        el.check.disabled = false;
        el.check.removeAttribute('aria-busy');
      }
    };

    el.check.addEventListener('click', checkCode);
    el.input.addEventListener('keydown', (event) => {
      // Enter inside the code field checks the code. It must not submit the
      // surrounding form: in the lead form that is a different form and a
      // different question, and in the Pro form the step sits inside the form
      // and Enter would send the application.
      if (event.key === 'Enter') {
        event.preventDefault();
        checkCode();
      }
    });
    // Wrapped, not passed: as a listener it would receive the click Event as
    // its options object, which happens to work — Event has no `focus` — and
    // would stop working the day it did.
    el.edit.addEventListener('click', () => backToAsk());

    el.none.addEventListener('change', async () => {
      clearErr();
      if (!el.none.checked) {
        hasReferrer = false;
        return;
      }
      // The code is theirs and is read at the moment it is used, not cached
      // at render: a merchant can change it in the MV Qore admin between the
      // page loading and this being ticked.
      const code = bypass && bypass.defaultCode;
      if (!code) return;
      try {
        const result = await sdk.validateReferrer(code);
        sdk.persistReferrer(result);
        paintReferrer(result);
        announceReferrer(result);
      } catch (error) {
        console.warn('[pruvit] referrer bypass code ' + code + ' did not validate:', error);
      }
    });

    // Reveal the bypass option only if the merchant has it on. getSettings
    // never rejects — it resolves null when it cannot be read — so a failure
    // here leaves the option hidden, which is the side to fail on.
    const settings = await sdk.getSettings();
    bypass = (settings && settings.referrerBypass) || null;
    if (bypassOn() && el.noneWrap) {
      // Their label is not used. Copy belongs to the theme (Craig, 2026-09-20)
      // and pruvit.lead.ref_none is translated with everything else; their
      // setting is one string per store and cannot serve two forms.
      el.noneWrap.hidden = false;
    }

    // Attribution is read from storage and attached by the SDK whether or not
    // the step is on screen; this only shows who it will be.
    const active = await sdk.getActiveReferrer();
    if (active) paintReferrer(active);

    // Changes made in the header bar, or in another form on the same page.
    // Back to the question when it is cleared: leaving a name on screen that
    // the bar has just dropped is the same inconsistency the other way round.
    document.addEventListener(REFERRER_EVENT, (event) => {
      const result = event.detail && event.detail.result;
      if (result) paintReferrer(result);
      else backToAsk({ focus: false });
    });

    return {
      has: () => hasReferrer,
      // The submit gate's half. The reason goes on the step, not at the foot
      // of the form: the fix is up here, so this is where the eye is sent.
      // Centred, so the label above the field is in view too, and instant
      // under reduced motion. The caret goes in last with preventScroll, or
      // focus() would jump the page itself and cut the smooth scroll short.
      require: () => {
        if (hasReferrer) return;
        el.ok.hidden = true;
        el.ask.hidden = false;
        showErr(askMessage());
        step.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        el.input.focus({ preventScroll: true });
      },
    };
  };

  window.pruvitReferrerStep = { mount };
})();
